export type SimulationPricePoint = {
  date: string;
  close: number;
};

export type SimulationDividendEvent = {
  date: string;
  amountPerShare: number;
};

export type SimulationPoint = {
  date: string;
  closePrice: number;
  sharesPriceOnly: number;
  sharesWithReinvestment: number;
  priceOnlyValue: number;
  reinvestedValue: number;
  creditedDividends: number;
};

export type SimulationResult = {
  points: SimulationPoint[];
  actualStartDate: string;
  actualEndDate: string;
  initialAmount: number;
};

const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;

const toDay = (value: string) => value.slice(0, 10);

const isValidDay = (value: string) => DAY_RE.test(value);

const compareDay = (left: string, right: string) => left.localeCompare(right);

export const buildFundInvestmentSimulation = ({
  prices,
  dividends,
  startDate,
  endDate,
  initialAmount,
}: {
  prices: SimulationPricePoint[];
  dividends: SimulationDividendEvent[];
  startDate: string;
  endDate: string;
  initialAmount?: number;
}): SimulationResult | null => {
  if (!isValidDay(startDate) || !isValidDay(endDate) || compareDay(startDate, endDate) > 0) {
    return null;
  }

  const amount = Number.isFinite(initialAmount) && (initialAmount ?? 0) > 0
    ? (initialAmount as number)
    : 100;

  const normalizedPrices = prices
    .map((item) => ({ date: toDay(item.date), close: item.close }))
    .filter((item) => isValidDay(item.date) && Number.isFinite(item.close) && item.close > 0)
    .sort((a, b) => compareDay(a.date, b.date));

  const windowPrices = normalizedPrices.filter(
    (item) => compareDay(item.date, startDate) >= 0 && compareDay(item.date, endDate) <= 0,
  );

  if (windowPrices.length === 0) {
    return null;
  }

  const actualStartDate = windowPrices[0].date;
  const actualEndDate = windowPrices[windowPrices.length - 1].date;

  const normalizedDividends = dividends
    .map((item) => ({ date: toDay(item.date), amountPerShare: item.amountPerShare }))
    .filter(
      (item) =>
        isValidDay(item.date) &&
        Number.isFinite(item.amountPerShare) &&
        item.amountPerShare > 0 &&
        compareDay(item.date, actualStartDate) >= 0 &&
        compareDay(item.date, actualEndDate) <= 0,
    )
    .sort((a, b) => compareDay(a.date, b.date));

  const sharesBase = amount / windowPrices[0].close;
  let sharesWithReinvestment = sharesBase;
  let pendingCash = 0;
  let dividendIndex = 0;

  const points: SimulationPoint[] = windowPrices.map((pricePoint) => {
    let creditedDividends = 0;
    while (
      dividendIndex < normalizedDividends.length &&
      compareDay(normalizedDividends[dividendIndex].date, pricePoint.date) <= 0
    ) {
      const dividend = normalizedDividends[dividendIndex];
      const credited = sharesWithReinvestment * dividend.amountPerShare;
      pendingCash += credited;
      creditedDividends += credited;
      dividendIndex += 1;
    }

    if (pendingCash > 0) {
      sharesWithReinvestment += pendingCash / pricePoint.close;
      pendingCash = 0;
    }

    return {
      date: pricePoint.date,
      closePrice: pricePoint.close,
      sharesPriceOnly: sharesBase,
      sharesWithReinvestment,
      priceOnlyValue: sharesBase * pricePoint.close,
      reinvestedValue: sharesWithReinvestment * pricePoint.close,
      creditedDividends,
    };
  });

  return {
    points,
    actualStartDate,
    actualEndDate,
    initialAmount: amount,
  };
};
